// Versão: 2.02
// Data..: 2026-09-29
// Editor de texto rico (contentEditable) com formatação avançada e upload de imagem.
// v2.02: prop onSubmit — Ctrl+Enter (ou ⌘+Enter) dispara o envio (resposta rápida).
// v2.01: prop publicUpload usa o endpoint de upload público (portal sem login).
// Suporta: negrito, itálico, sublinhado, título, listas (com/sem ordem), citação,
// link, bloco de código e imagens (S3 via presign). O corpo HTML é sanitizado no backend.

import { useEffect, useRef, useState } from 'react';
import { uploadFile, uploadPublicImage } from '../lib/upload.js';
import { useDialog } from '../context/DialogContext.js';
import { config } from '../config.js';

interface RichEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  // Quando true, usa o endpoint de upload público (portal sem login).
  publicUpload?: boolean;
  // Disparado ao pressionar Ctrl+Enter (ou ⌘+Enter). Usado para enviar a resposta.
  onSubmit?: () => void;
}

export function RichEditor({ value, onChange, placeholder, minHeight = 140, publicUpload = false, onSubmit }: RichEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { prompt } = useDialog();

  // Sincroniza o valor externo apenas quando difere (evita resetar o cursor a cada tecla).
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value;
    }
  }, [value]);

  const emit = () => onChange(ref.current?.innerHTML ?? '');

  // Executa um comando de formatação, opcionalmente com valor (ex.: formatBlock).
  const exec = (cmd: string, arg?: string) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
  };

  // Insere um link a partir da seleção atual (pede a URL num modal da aplicação).
  // A seleção do contentEditable é salva antes de abrir o modal e restaurada depois,
  // pois o foco no diálogo faz o editor perder o range selecionado.
  const insertLink = async () => {
    const sel = window.getSelection();
    const savedRange = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
    const url = await prompt({
      title: 'Inserir link',
      label: 'Endereço (URL)',
      defaultValue: 'https://',
      placeholder: 'https://exemplo.com',
      confirmLabel: 'Inserir',
    });
    if (!url) return;
    ref.current?.focus();
    if (savedRange) {
      const s = window.getSelection();
      s?.removeAllRanges();
      s?.addRange(savedRange);
    }
    document.execCommand('createLink', false, url);
    emit();
  };

  // Envolve a seleção num bloco de código (<pre><code>…</code></pre>).
  const insertCodeBlock = () => {
    const sel = window.getSelection();
    const text = sel && !sel.isCollapsed ? sel.toString() : '';
    const escaped = (text || 'código')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    exec('insertHTML', `<pre class="code-block"><code>${escaped}</code></pre><p><br/></p>`);
  };

  // Faz upload de uma imagem e insere no ponto do cursor.
  const insertImage = async (file: File) => {
    setUploading(true);
    try {
      const att = publicUpload ? await uploadPublicImage(file) : await uploadFile(file);
      // Codifica a chave S3 (que contém "/") em base64url para caber num único segmento.
      const encoded = btoa(att.s3Key).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      const url = `${config.apiUrl}/public/attachments/${encoded}`;
      exec('insertHTML', `<img src="${url}" alt="${att.fileName}" style="max-width:100%;border-radius:8px;margin:8px 0" />`);
    } finally {
      setUploading(false);
    }
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'));
    if (item) {
      e.preventDefault();
      const file = item.getAsFile();
      if (file) void insertImage(file);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    const file = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith('image/'));
    if (file) {
      e.preventDefault();
      void insertImage(file);
    }
  };

  // Botão de barra de ferramentas padronizado.
  const Btn = ({ onClick, title, children, style }: { onClick: () => void; title: string; children: React.ReactNode; style?: React.CSSProperties }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className="btn btn-sm btn-ghost"
      style={{ minWidth: 32, height: 30, padding: '0 6px', ...style }}
    >
      {children}
    </button>
  );

  const Sep = () => <span style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 3px' }} />;

  return (
    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
      <div
        className="flex items-center gap-1"
        style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', flexWrap: 'wrap' }}
      >
        <Btn title="Negrito" onClick={() => exec('bold')} style={{ fontWeight: 700 }}>B</Btn>
        <Btn title="Itálico" onClick={() => exec('italic')} style={{ fontStyle: 'italic' }}>I</Btn>
        <Btn title="Sublinhado" onClick={() => exec('underline')} style={{ textDecoration: 'underline' }}>U</Btn>
        <Sep />
        <Btn title="Título" onClick={() => exec('formatBlock', 'H3')}>H</Btn>
        <Btn title="Texto normal" onClick={() => exec('formatBlock', 'P')}>¶</Btn>
        <Btn title="Citação" onClick={() => exec('formatBlock', 'BLOCKQUOTE')}>❝</Btn>
        <Sep />
        <Btn title="Lista com marcadores" onClick={() => exec('insertUnorderedList')}>•</Btn>
        <Btn title="Lista numerada" onClick={() => exec('insertOrderedList')}>1.</Btn>
        <Sep />
        <Btn title="Inserir link" onClick={insertLink}>🔗</Btn>
        <Btn title="Bloco de código" onClick={insertCodeBlock}>{'</>'}</Btn>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void insertImage(f); e.target.value = ''; }} />
        <Btn title="Inserir imagem" onClick={() => fileRef.current?.click()}>🖼</Btn>
        {uploading && <span className="spinner" style={{ width: 14, height: 14, marginLeft: 6 }} />}
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          // Ctrl+Enter (ou ⌘+Enter no Mac) envia — atalho de resposta rápida.
          if (onSubmit && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            emit(); // garante que o onChange propague o conteúdo atual antes de enviar
            onSubmit();
          }
        }}
        data-placeholder={placeholder}
        className="rich-body"
        style={{
          minHeight, padding: '12px', outline: 'none', fontSize: 14, lineHeight: 1.6,
          background: 'var(--surface)', color: 'var(--text)',
        }}
        suppressContentEditableWarning
      />
    </div>
  );
}
